// @flow

import React, {useState} from 'react';

import Badge from 'sail/Badge';
import Box from 'sail/Box';
import FlagIcon, {FLAG_COUNTRY_CODES} from 'sail/FlagIcon';
import Grid, {GridItem} from 'sail/Grid';
import Group from 'sail/Group';
import {MenuItem} from 'sail/Menu';
import MenuContainer from 'sail/MenuContainer';
import {BodyAlt} from 'sail/Text';
import {useAccount, useNavigation} from 'docs/data';

import type {Country} from 'docs/data';
import {SelectorLink} from './SelectorLink';

type CountrySelectorMenuItemProps = {|
  country: Country,
  onClick: () => void,
|};

const CountrySelectorMenuItem = ({
  country,
  onClick,
}: CountrySelectorMenuItemProps) => {
  const previewBadge = country.beta && <Badge label="Preview" />;

  return (
    <MenuItem onClick={onClick}>
      <Group direction="horizontal" spacing={8} alignItems="center">
        <FlagIcon countryCode={FLAG_COUNTRY_CODES[country.code]} />
        <BodyAlt>{country.name}</BodyAlt>
        {previewBadge}
      </Group>
    </MenuItem>
  );
};

type CountrySelectorMenuProps = {|
  countries: Array<Country>,
  onCountrySelected: (country: Country) => void,
|};

const CountrySelectorMenu = ({
  countries,
  onCountrySelected,
}: CountrySelectorMenuProps) => {
  const getColumn = (index) => (index % 2 === 0 ? 1 : 7);
  const getRow = (index) => Math.ceil((index + 1) / 2);

  return (
    <Box
      padding={{vertical: 16, horizontal: 12}}
      style={{width: 'calc(100vw - 24px)', maxWidth: '450px'}}
    >
      <Grid columnGap={4}>
        {countries.map((country, index) => (
          <GridItem
            key={country.code}
            column={getColumn(index)}
            row={getRow(index)}
            columnSpan={6}
          >
            <CountrySelectorMenuItem
              country={country}
              onClick={() => onCountrySelected(country)}
            />
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
};

type CountrySelectorProps = {|
  showLabel?: boolean,
|};

export const CountrySelector = ({showLabel = true}: CountrySelectorProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const {account, isLoading, setCountry} = useAccount();
  const {setNavigation} = useNavigation();

  const handleCountrySelected = (country: Country) => {
    setCountry(country, setNavigation);
    setShowMenu(false);
  };

  const menu = (
    <CountrySelectorMenu
      countries={account.availableCountries}
      onCountrySelected={handleCountrySelected}
    />
  );

  return (
    <MenuContainer
      contextMeetingPoint="topleft"
      layerMeetingPoint="bottomleft"
      offsetX={6}
      contents={menu}
      onMenuStateChange={(value) => setShowMenu(value)}
      showMenu={showMenu}
      autoflip={false}
    >
      <SelectorLink
        size="medium"
        color="gray"
        label={account.country.name}
        icon={account.country.code}
        pending={isLoading}
        showLabel={showLabel}
      />
    </MenuContainer>
  );
};
